import { HeatmapInts2D } from '@aipb/gulliver/castor/model';
import { CastorConfigRepository, SearchService } from '@aipb/gulliver/catalog/data-access';
import { CatalogEnvService } from '@aipb/gulliver/catalog/env';
import { OpenLayersModel } from '@aipb/shared/ui/open-layers';
import { createLineStringFeature, createPointFeature, createPolygonFeature } from '@aipb/shared/util/open-layers';
import { inject, Injectable } from '@angular/core';
import { Feature, Map, MapBrowserEvent, View } from 'ol';
import LayerSwitcher from 'ol-layerswitcher';
import { Control } from 'ol/control';
import { Coordinate } from 'ol/coordinate';
import { Geometry, LineString, Polygon } from 'ol/geom';
import Point from 'ol/geom/Point';
import { defaults, Interaction } from 'ol/interaction';
import { Group, Heatmap as HeatmapLayer } from 'ol/layer';
import BaseLayer from 'ol/layer/Base';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import { fromLonLat, transform } from 'ol/proj';
import RenderFeature from 'ol/render/Feature';
import OSM from 'ol/source/OSM';
import VectorSource from 'ol/source/Vector';
import { Circle, Fill, RegularShape, Stroke, Style, Text } from 'ol/style';
import { ResetZoomControl } from '../../controls/reset-zoom.control';
import { SearchMapRecordListDialogService } from '../../dialogs/search-map-record-list-dialog';

const FEATURE_POINT_TYPE = 'GulliverRecType';
const STYLE_RECORD = 'record';
const STYLE_EXTENT = 'extent';
const STYLE_GRID = 'grid';
const LAYER_NAME = 'layer_name';
const LAYER_RECORD_NAME = 'record';
const LAYER_HEATMAP_NAME = 'heatmap';

@Injectable()
export class SearchMapService extends OpenLayersModel {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _extentSource: VectorSource<any> = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _pointSource: VectorSource<any> = null;
  private _heatmapLayer: HeatmapLayer = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _heatmapSource: VectorSource<Point> = null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _gridSource: VectorSource<LineString> = null;

  private _recordSource: VectorSource<Point> = null;

  private searchMapRecordListDialog = inject(SearchMapRecordListDialogService);

  private _zoom = 2;
  private _center: number[] = [0, 0];
  private _extent: number[];
  private _showLayerSwitch = true;

  constructor(
    private search: SearchService,
    private castorConfigRepository: CastorConfigRepository,
    private catalogEnv: CatalogEnvService
  ) {
    super();
  }

  public updateMap(map: Map): void {
    this.setMap(map);

    this.fitToExtent();
  }

  public init(zoom: number, extent: number[], center: number[] = [0, 0], showLayerSwitcher = true): void {
    this._zoom = zoom;
    this._extent = extent;
    this._center = center;
    this._showLayerSwitch = showLayerSwitcher;

    this.initialize();
  }

  public fitToExtent(): void {
    if (this._extent && this.hasExtent()) {
      const geometry = this.getExtent().getGeometry();
      this._map.getView().fit(geometry);
    }
  }

  public initExtent(extent: number[]): void {
    this.clearExtent();

    if (!extent) {
      return;
    }

    const feature = createPolygonFeature([
      [
        fromLonLat([extent[1], extent[0]]),
        fromLonLat([extent[1], extent[2]]),
        fromLonLat([extent[3], extent[2]]),
        fromLonLat([extent[3], extent[0]]),
        fromLonLat([extent[1], extent[0]])
      ]
    ]);

    this.addExtent(feature);
  }

  public initPoints(points: number[][]): void {
    this.clearPoints();

    for (const point of points) {
      const feature = createPointFeature(point);

      feature.set(FEATURE_POINT_TYPE, 'GEO');
      feature.getGeometry().transform('EPSG:4326', 'EPSG:3857');

      this.addPoint(feature);
    }
  }

  public initHeatmap(heatmap: HeatmapInts2D): void {
    this.clearHeatmap();

    if (!heatmap || !this.map) return;

    const data = heatmap;

    const xChange = (data.maxX - data.minX) / data.columns;
    const yChange = (data.maxY - data.minY) / data.rows;

    let max = 0;
    for (let row = 0; row < data.rows; row++) {
      if (!data.counts_ints2D || !data.counts_ints2D[row]) continue;

      for (let col = 0; col < data.columns; col++) {
        if (data.counts_ints2D[row][col] > max) {
          max = data.counts_ints2D[row][col];
        }
      }
    }

    const leftCoordinates = transform([data.minX, data.maxY], 'EPSG:4326', 'EPSG:3857');
    const left = this.map.getPixelFromCoordinate(leftCoordinates);
    let rightCoordinates: Coordinate;
    let radius: number;

    if (data.columns >= data.rows) {
      const yStep = (data.maxY - data.minY) / data.rows;
      rightCoordinates = transform([data.minX, data.maxY - yStep], 'EPSG:4326', 'EPSG:3857');
      const right = this.map.getPixelFromCoordinate(rightCoordinates);
      radius = Math.abs(right[1] - left[1]);
    } else {
      const xStep = (data.maxX - data.minX) / data.columns;
      rightCoordinates = transform([data.minX + xStep, data.maxY], 'EPSG:4326', 'EPSG:3857');
      const right = this.map.getPixelFromCoordinate(rightCoordinates);
      radius = Math.abs(right[0] - left[0]);
    }

    this._heatmapLayer.setRadius(Math.max(radius / 2, 15));
    this._heatmapLayer.setBlur(radius / 1.5);

    for (let row = 0; row < data.rows; row++) {
      if (data.counts_ints2D && data.counts_ints2D[row]) {
        for (let col = 0; col < data.columns; col++) {
          if (data.counts_ints2D[row][col] <= 1) continue;

          const x = data.minX + xChange * col;
          const y = data.maxY - yChange * row;

          const feature = createPointFeature([x + xChange / 2, y - yChange / 2]);
          feature.set('size', data.counts_ints2D[row][col]);
          feature.set('max', max);
          feature.set('radius', radius);
          feature.set('xMin', x);
          feature.set('xMax', x + xChange);
          feature.set('yMin', y - yChange);
          feature.set('yMax', y);
          feature.getGeometry().transform('EPSG:4326', 'EPSG:3857');

          this.addHeatmapPoint(feature);
        }
      }
    }
  }

  public initGrid(heatmap: HeatmapInts2D): void {
    this.clearGrid();

    if (!heatmap) return;

    const data = heatmap;

    const xChange = (data.maxX - data.minX) / data.columns;
    const yChange = (data.maxY - data.minY) / data.rows;

    for (let row = 0; row < data.rows; row++) {
      for (let col = 0; col < data.columns; col++) {
        const x = data.minX + xChange * col;
        const y = data.maxY - yChange * row;

        const verticalLine = createLineStringFeature([
          [x, data.minY],
          [x, data.maxY]
        ]);
        verticalLine.getGeometry().transform('EPSG:4326', 'EPSG:3857');
        this.addGridLine(verticalLine);

        const horizontalLine = createLineStringFeature([
          [data.minX, y],
          [data.maxX, y]
        ]);
        horizontalLine.getGeometry().transform('EPSG:4326', 'EPSG:3857');
        this.addGridLine(horizontalLine);
      }
    }
  }

  public initRecords(heatmap: HeatmapInts2D): void {
    this.clearRecords();

    if (!heatmap) return;

    const data = heatmap;

    const xChange = (data.maxX - data.minX) / data.columns;
    const yChange = (data.maxY - data.minY) / data.rows;

    for (let row = 0; row < data.rows; row++) {
      if (data.counts_ints2D && data.counts_ints2D[row]) {
        for (let col = 0; col < data.columns; col++) {
          if (
            data.counts_ints2D[row][col] > (this.catalogEnv.env.mapSearch.recordMarker.threshold || 1) ||
            data.counts_ints2D[row][col] === 0
          )
            continue;

          const x = data.minX + xChange * col;
          const y = data.maxY - yChange * row;

          const feature = createPointFeature([x + xChange / 2, y - yChange / 2]);
          feature.set('size', data.counts_ints2D[row][col]);
          feature.set('xMin', x);
          feature.set('xMax', x + xChange);
          feature.set('yMin', y - yChange);
          feature.set('yMax', y);
          feature.getGeometry().transform('EPSG:4326', 'EPSG:3857');

          this.addRecordPoint(feature);
        }
      }
    }
  }

  public getExtent(): Feature<Polygon> {
    return this._extentSource.getFeatures()[0];
  }

  public hasExtent(): boolean {
    return this._extentSource.getFeatures().length > 0;
  }

  public clearGrid(): void {
    if (!this._gridSource) return;

    this._gridSource.clear();
  }

  public clearPoints(): void {
    if (!this._pointSource) return;

    this._pointSource.clear();
  }

  public clearExtent(): void {
    if (!this._extentSource) return;

    this._extentSource.clear();
  }

  public clearHeatmap(): void {
    if (!this._heatmapSource) return;

    this._heatmapSource.clear();
  }

  public clearRecords(): void {
    if (!this._recordSource) return;

    this._recordSource.clear();
  }

  public setZoom(zoom: number) {
    if (!this._map) return;

    this._map.getView().setZoom(zoom);
  }

  public getZoom(): number {
    if (!this._map) return 0;

    return this._map.getView().getZoom();
  }

  public setCenter(center: number[]) {
    if (!this._map) return;

    this._map.getView().setCenter(center);
  }

  public getCenter(): number[] {
    if (!this._map) return [0, 0];

    return this._map.getView().getCenter();
  }

  public getSize(): number[] {
    return this._map ? this._map.getSize() : [0, 0];
  }

  public getView(): View {
    if (!this._map) return null;

    return this._map.getView();
  }

  onMapClick(evt: MapBrowserEvent<never>) {
    const selectedFeatures: (RenderFeature | Feature<Geometry>)[] = [];

    this._map.forEachFeatureAtPixel(
      evt.pixel,
      function (feature) {
        //do something
        selectedFeatures.push(feature);
      },
      {
        layerFilter: function (layer) {
          return layer.get(LAYER_NAME) === LAYER_RECORD_NAME || layer.get(LAYER_NAME) === LAYER_HEATMAP_NAME;
        }
      }
    );

    if (selectedFeatures.length > 0) {
      const feature = selectedFeatures[0];

      const minX = feature.get('xMin');
      const maxX = feature.get('xMax');
      const minY = feature.get('yMin');
      const maxY = feature.get('yMax');
      const rectangle = [minY, minX, maxY, maxX];

      this.search
        .solrSearch({
          query: '*:*',
          spatialFilter: {
            spatialType: 'rectangle',
            spatialField: 'Coordinates',
            rectangle: rectangle.join(',')
          }
        })
        .subscribe((data) => {
          this.searchMapRecordListDialog.open({
            items: data.recordList,
            coreId: this.castorConfigRepository.getActiveCoreId(),
            rectangle: rectangle
          });
        });
    }
  }

  public destroy(): void {
    this.searchMapRecordListDialog.close();
  }

  getMinZoom() {
    if (!this._map) return 0;

    return this._map.getView().getMinZoom();
  }

  getMaxZoom() {
    if (!this._map) return 0;

    return this._map.getView().getMaxZoom();
  }

  protected override initControls(): Control[] {
    const controls: Control[] = [];

    if (this._showLayerSwitch) {
      controls.push(
        new LayerSwitcher({
          reverse: true,
          groupSelectStyle: 'children'
        })
      );
    }

    controls.push(
      new ResetZoomControl({}, () => {
        this.setZoom(this._zoom);
        this.setCenter(this._center);

        const extent = this.generateExtent();

        if (extent) {
          this.map.getView().fit(extent);
        }

        this.searchMapRecordListDialog.close();
      })
    );

    return controls;
  }

  protected override initStyles(): { [p: string]: Style } {
    return {
      GEO: new Style({
        image: new Circle({
          radius: 9,
          fill: new Fill({ color: 'red' })
        })
      }),
      korporace: new Style({
        image: new Circle({
          radius: 9,
          fill: new Fill({ color: 'blue' })
        })
      }),
      corner: new Style({
        image: new Circle({
          radius: 9,
          fill: new Fill({ color: 'yellow' })
        })
      }),
      rectangle: new Style({
        image: new RegularShape({
          fill: new Fill({ color: 'red' }),
          stroke: new Stroke({ color: 'black', width: 2 }),
          radius: 10 / Math.SQRT2,
          radius2: 10,
          points: 4,
          angle: 0,
          scale: [1, 0.5]
        })
      }),
      [STYLE_EXTENT]: new Style({
        stroke: new Stroke({ color: '#00FF00', width: 3 }),
        fill: new Fill({ color: 'transparent' })
      }),
      [STYLE_GRID]: new Style({
        stroke: new Stroke({ color: 'blue', width: 1 })
      })
    };
  }

  protected override initDynamicStyles(): { [p: string]: (feature: RenderFeature | Feature<Geometry>) => Style } {
    return {
      [STYLE_RECORD]: (feature) => {
        const radius = this.catalogEnv.env.mapSearch.recordMarker.size || 9;
        return new Style({
          image: new Circle({
            radius: radius,
            fill: new Fill({ color: 'white' }),
            stroke: new Stroke({ color: 'red' })
          }),
          text: new Text({ text: feature.get('size') + '', font: radius * 2 - 5 + 'px/1 sans-serif' })
        });
      }
    };
  }

  protected override initLayers(): BaseLayer[] {
    const extent = this.generateExtent();

    this._heatmapSource = new VectorSource();

    this._heatmapLayer = new HeatmapLayer({
      properties: {
        title: 'Heatmapa'
      },
      source: this._heatmapSource,
      blur: 10,
      radius: 20,
      weight: function (feature) {
        return 0.5 + feature.get('size') / feature.get('max');
      }
    });
    this._heatmapLayer.set(LAYER_NAME, LAYER_HEATMAP_NAME);

    this._gridSource = new VectorSource();

    const gridLayer = new VectorLayer({
      properties: {
        title: 'Grid'
      },
      visible: false,
      source: this._gridSource,
      style: this.getStyle(STYLE_GRID)
    });

    this._recordSource = new VectorSource();

    const recordLayer = new VectorLayer({
      properties: {
        title: 'Záznamy'
      },
      source: this._recordSource,
      style: (feature) => {
        return this.getDynamicStyle(STYLE_RECORD, feature);
      }
    });
    recordLayer.set(LAYER_NAME, LAYER_RECORD_NAME);

    this._pointSource = new VectorSource();

    const pointLayer = new VectorLayer({
      source: this._pointSource,
      style: (feature) => {
        const type = feature.get(FEATURE_POINT_TYPE);

        return this.getStyle(type);
      }
    });

    this._extentSource = new VectorSource();
    const extentLayer = new VectorLayer({
      source: this._extentSource,
      style: this.getStyle(STYLE_EXTENT)
    });

    return [
      new TileLayer({
        source: new OSM({
          wrapX: false
        })
      }),
      pointLayer,
      new Group({
        properties: {
          title: 'Vrstvy'
        },
        layers: [this._heatmapLayer, recordLayer, gridLayer]
      }),
      extentLayer
    ];
  }

  protected override initInteractions(): Interaction[] {
    return defaults().getArray();
  }

  protected override initView(): View {
    return new View({
      center: this._center,
      zoom: this._zoom
    });
  }

  private addPoint(feature: Feature<Point>): void {
    if (!this._pointSource) return;

    this._pointSource.addFeature(feature);
  }

  private addExtent(feature: Feature<Polygon>): void {
    if (!this._extentSource) return;

    this._extentSource.addFeature(feature);
  }

  private addHeatmapPoint(feature: Feature<Point>): void {
    if (!this._heatmapSource) return;

    this._heatmapSource.addFeature(feature);
  }

  private addGridLine(feature: Feature<LineString>): void {
    if (!this._gridSource) return;

    this._gridSource.addFeature(feature);
  }

  private addRecordPoint(feature: Feature<Point>): void {
    if (!this._recordSource) return;

    this._recordSource.addFeature(feature);
  }

  private generateExtent(): number[] {
    return this._extent
      ? [...fromLonLat([this._extent[1], this._extent[0]]), ...fromLonLat([this._extent[3], this._extent[2]])]
      : undefined;
  }
}
